import { GridStackMoveOpts, GridStackNode, GridStackPosition, GridItemHTMLElement } from 'gridstack/dist/types';
import { GridStackEngine, GridStack } from 'gridstack';

interface BaseNodeConfig {
	leftNeighbours: GridStackNode[];
	rightNeighbours: GridStackNode[];
	topNeighbours: GridStackRow[];
	bottomNeighbours: GridStackRow[];
	currentRow: GridStackRow;
	startX: number;
	startY: number;
	startW: number;
	startH: number;
	availableWLeft: number;
	availableWRight: number;
	availableYTop: number;
}

interface RowResizingConfig extends BaseNodeConfig {
	border?: 'right' | 'left' | 'up' | 'down' | undefined;
}

export interface DraggingConfig extends BaseNodeConfig {}

interface GridStackRow {
	nodes: GridStackNode[];
	y: number;
	height: number;
	minHeight: number;
	hasWrongHeight: boolean;
	availableSpace: number;
}

export class MxGridStackEngine extends GridStackEngine {
	private rowResizingConfig?: RowResizingConfig;
	private draggingConfig?: DraggingConfig;

	public get resizingBorder() {
		return this.rowResizingConfig?.border;
	}

	private getNearestRow(y: number): GridStackRow {
		const rows = this.getAllRows();
		const lastRow = rows.at(-1);
		if (typeof lastRow !== 'undefined' && lastRow.y + lastRow.height <= y) {
			return this.getGridStackRow(lastRow.y + lastRow.height);
		} else {
			return rows.filter((row) => row.y <= y).at(-1) || this.getGridStackRow(y);
		}
	}

	private getNodesMinWidth(nodes: GridStackNode[]) {
		return nodes.reduce((fullMinWidth, node) => fullMinWidth + (node.minW ?? 1), 0);
	}

	private getGridStackRow(y: number): GridStackRow {
		const nodes = this.nodes.filter((node) => node.y === y).toSorted(({ x: xA = 0 }, { x: xB = 0 }) => xA - xB);
		const row: GridStackRow = {
			y,
			nodes,
			height: 1,
			minHeight: nodes.reduce((minHeight, node) => Math.max(minHeight, node.minH ?? 1), 1),
			hasWrongHeight: false,
			availableSpace: this.column - this.getNodesMinWidth(nodes),
		};

		const existMinHeight = row.nodes.reduce((minHeight, node) => Math.min(minHeight, node.h ?? 1), row.nodes[0]?.h ?? 1);
		const existMaxHeight = row.nodes.reduce((maxHeight, node) => Math.max(maxHeight, node.h ?? 1), row.nodes[0]?.h ?? 1);

		row.height = Math.max(row.minHeight, existMaxHeight);
		row.hasWrongHeight = existMinHeight < row.minHeight || existMaxHeight !== existMinHeight;
		return row;
	}

	private getAllRows() {
		const rowsMap: Record<number, GridStackRow> = {};

		this.nodes.forEach((node) => {
			const y = node.y ?? 0;
			rowsMap[y] = rowsMap[y] || this.getGridStackRow(y);
		});

		return Object.values(rowsMap).toSorted(({ y: aY }, { y: bY }) => aY - bY);
	}

	private getNeighbours(centerNode: GridStackNode) {
		const rows = this.getAllRows();
		const topNeighbours = rows.filter((row) => row.y < (centerNode.y ?? 0));
		const bottomNeighbours = rows.filter((row) => row.y > (centerNode.y ?? 0));
		const currentRow = rows.find((row) => row.y === (centerNode.y ?? 0))!;

		const neighbours = currentRow.nodes.filter((node) => node !== centerNode).toSorted(({ x: xA = 0 }, { x: xB = 0 }) => xA - xB);
		const leftNeighbours = neighbours.filter((node) => (node.x ?? 0) < (centerNode.x ?? 0));
		const rightNeighbours = neighbours.filter((node) => (node.x ?? 0) >= (centerNode.x ?? 0));

		return { neighbours, leftNeighbours, rightNeighbours, topNeighbours, bottomNeighbours, currentRow };
	}

	private getNodeConfig(node: GridStackNode): BaseNodeConfig {
		const { leftNeighbours, rightNeighbours, topNeighbours, bottomNeighbours, currentRow } = this.getNeighbours(node);

		const startX = node.x ?? 0;
		const startY = node.y ?? 0;
		const startW = node.w ?? 0;
		const startH = node.h ?? 0;

		return {
			leftNeighbours,
			rightNeighbours,
			topNeighbours,
			bottomNeighbours,
			currentRow,
			startW,
			startX,
			startY,
			startH,
			availableWLeft: startW + startX - this.getNodesMinWidth(leftNeighbours) + 1,
			availableWRight: startW + (this.column - this.getNodesMinWidth(rightNeighbours) - (startW + startX)) + 1,
			availableYTop: startH + startY - topNeighbours.length + 1,
		};
	}

	public enableRowResizingMode(resizingNode: GridStackNode) {
		this.rowResizingConfig = this.getNodeConfig(resizingNode);
	}

	public disableRowResizingMode() {
		this.rowResizingConfig = undefined;
	}

	public enableDragIntoRowMode(draggingNode: GridStackNode) {
		this.draggingConfig = this.getNodeConfig(draggingNode);
	}

	public disableDragIntoRowMode() {
		this.draggingConfig = undefined;
	}

	private resizeNeighbours(node: GridStackNode) {
		if (!this.rowResizingConfig) {
			return;
		}
		if (this.rowResizingConfig.border === 'left' && this.rowResizingConfig.leftNeighbours.length) {
			this.justifyNodes(this.rowResizingConfig.leftNeighbours, 0, node.x ?? 0, node.y ?? 0, node.h ?? 1);
		} else if (this.rowResizingConfig.border === 'right' && this.rowResizingConfig.rightNeighbours.length) {
			this.justifyNodes(this.rowResizingConfig.rightNeighbours, (node.x ?? 0) + (node.w ?? 0), this.column - (node.w ?? 0) - (node.x ?? 0), node.y ?? 0, node.h ?? 1);
		} else if (this.rowResizingConfig.border === 'up') {
			this.justifyRows(this.rowResizingConfig.topNeighbours, 0, node.y ?? 0);
			this.justifyRows([this.rowResizingConfig.currentRow], node.y ?? 0, node.h ?? 0);
		} else if (this.rowResizingConfig.border === 'down') {
			this.justifyRows([this.rowResizingConfig.currentRow], node.y ?? 0, node.h ?? 0);
			this.justifyRows(this.rowResizingConfig.bottomNeighbours, (node.y ?? 0) + (node.h ?? 0));
		}
	}

	private justifyRows(rows: GridStackRow[], startY: number, fullHeight?: number) {
		this.getJustifiedRowSizes(rows, startY, fullHeight).forEach(({ row, y, h }) => {
			row.nodes.forEach((node) => super.moveNode(node, { y, h }));
		});
	}

	private getJustifiedRowSizes(rows: GridStackRow[], startY: number, fullHeight?: number) {
		const rowsHeight = rows.reduce((width, row) => width + row.height, 0);

		let prevSize: { y: number; h: number; row: GridStackRow };
		let sizes: (typeof prevSize)[] = [];

		if (typeof fullHeight === 'number') {
			let heightToChange = fullHeight - rowsHeight;
			rows.toSorted(({ y: hA = 0 }, { y: hB = 0 }) => (-heightToChange >= rows.length ? hA - hB : hB - hA)).forEach((row, index) => {
				const rowsLeft = rows.length - index;
				const currentHeight = row.height;
				let diffH = 0;

				diffH = Math.max(Math.floor(heightToChange / rowsLeft), heightToChange);
				if (currentHeight <= -diffH) {
					diffH = -currentHeight + 1;
				}

				const size = {
					y: 0,
					h: currentHeight + diffH,
					row,
				};

				heightToChange -= diffH;
				sizes.push(size);
			});
		} else {
			sizes = rows.map((row) => ({
				row,
				y: 0,
				h: row.height,
			}));
		}

		rows.forEach((row) => {
			const size = sizes.find((size) => size.row === row);
			if (size) {
				size.y = prevSize ? (prevSize.y ?? 0) + (prevSize.h ?? 0) : startY;
				prevSize = size;
			}
		});

		return sizes.toSorted(({ y: yA = 0 }, { y: yB = 0 }) => yA - yB);
	}

	private justifyNodes(nodes: GridStackNode[], startX: number, fullWidth: number, y: number, height: number) {
		this.getJustifiedSizes(nodes, startX, fullWidth).forEach(({ node, x, w }) => {
			super.moveNode(node, { x, w, h: height, y });
		});
	}

	private getJustifiedSizes(nodes: GridStackNode[], startX: number, fullWidth: number) {
		const nodesWidth = nodes.reduce((width, node) => width + (node.w ?? 1), 0);
		let widthToChange = fullWidth - nodesWidth;

		let prevSize: { x: number; w: number; node: GridStackNode };
		const sizes: (typeof prevSize)[] = [];

		if (widthToChange >= 0) {
			nodes
				.toSorted(({ w: wA = 0 }, { w: wB = 0 }) => wA - wB)
				.forEach((node, index) => {
					const nodesLeft = nodes.length - index;
					const currentWidth = node.w ?? 0;
					let diffW = 0;
					diffW = Math.min(Math.ceil(widthToChange / nodesLeft), widthToChange);

					const size = {
						x: 0,
						w: currentWidth + diffW,
						node,
					};

					widthToChange -= diffW;
					sizes.push(size);
				});
		} else {
			const resizableNodes = nodes.filter((node) => (node.minW ?? 1) > (node.w ?? 1));

			nodes
				.toSorted(({ w: wA = 0 }, { w: wB = 0 }) => (-widthToChange >= resizableNodes.length ? wA - wB : wB - wA))
				.forEach((node, index) => {
					const nodesLeft = nodes.length - index;
					const currentWidth = node.w ?? 1;
					let diffW = 0;

					const isResizable = (node.minW ?? 1) < currentWidth;

					if (isResizable) {
						diffW = Math.max(Math.floor(widthToChange / nodesLeft), widthToChange);
						if (currentWidth <= -diffW) {
							diffW = -currentWidth + 1;
						}
					}

					const size = {
						x: 0,
						w: currentWidth + diffW,
						node,
					};
					widthToChange -= diffW;
					sizes.push(size);
				});
		}

		nodes.forEach((node) => {
			const size = sizes.find((size) => size.node === node);
			if (size) {
				size.x = prevSize ? (prevSize.x ?? 0) + (prevSize.w ?? 0) : startX;
				prevSize = size;
			}
		});

		return sizes.toSorted(({ x: xA = 0 }, { x: xB = 0 }) => xA - xB);
	}

	private justifyRow(row: GridStackRow, y?: number, height?: number) {
		this.justifyNodes(row.nodes, 0, this.column, y ?? row.y, height ?? row.height);
	}

	public justify() {
		const rows = this.getAllRows();
		let y = 0;
		rows.forEach((row) => {
			this.justifyRow(row, y);
			y += row.height;
		});
	}

	removeNode(node: GridStackNode, removeDOM?: boolean, triggerEvent?: boolean): GridStackEngine {
		const row = this.getGridStackRow(node.y ?? 0);
		const result = super.removeNode(node, removeDOM, triggerEvent);
		row.nodes.splice(row.nodes.indexOf(node), 1);
		this.justifyRow(row);
		return result;
	}

	collideAll(): GridStackNode[] {
		return [];
	}

	/** true if x,y or w,h are different after clamping to min/max */
	changedPosConstrain(node: GridStackNode, p: GridStackPosition): boolean {
		const result = super.changedPosConstrain(node, p);
		if (!result) {
			return result;
		}

		if (!this.rowResizingConfig && !this.draggingConfig) {
			return result;
		} else if (this.rowResizingConfig) {
			if (!this.rowResizingConfig.border) {
				this.rowResizingConfig.border = node.x !== p.x ? 'left' : node.w !== p.w ? 'right' : node.y !== p.y ? 'up' : node.h !== p.h ? 'down' : undefined;
			}

			const h = p.h ?? 1;
			if (this.rowResizingConfig.border === 'left' || this.rowResizingConfig.border === 'right') {
				if ((this.rowResizingConfig.border === 'left' && node.x === 0) || (this.rowResizingConfig.border === 'right' && (node.x ?? 0) + (node.w ?? 1) === this.column)) {
					return false;
				}
				const availableW = this.rowResizingConfig.border === 'left' ? this.rowResizingConfig.availableWLeft : this.rowResizingConfig.availableWRight;
				return (p.w ?? 1) < availableW;
			} else if (this.rowResizingConfig.border === 'up') {
				return h < this.rowResizingConfig.availableYTop && h >= this.rowResizingConfig.currentRow.minHeight;
			} else if (this.rowResizingConfig.border === 'down') {
				return h >= this.rowResizingConfig.currentRow.minHeight;
			}
		}

		return true;
	}

	private insertNodeBeforeRow(node: GridStackNode, row: GridStackRow) {
		const newRow: GridStackRow = {
			y: row.y,
			nodes: [node],
			height: node.h ?? 1,
			minHeight: node.minH ?? 1,
			hasWrongHeight: false,
			availableSpace: 0,
		};
		const rows = this.getAllRows();
		const index = rows.findIndex((existRow) => existRow.y === row.y);
		if (index < 0) {
			return;
		}

		let diffY = 0;
		rows.forEach((existRow) => {
			if (existRow.y === newRow.y) {
				diffY = existRow.height;
			}
			existRow.y = existRow.y + diffY;
			const index = existRow.nodes.indexOf(node);
			if (index >= 0) {
				existRow.nodes.splice(index, 1);
			}
		});

		rows.splice(index, 0, newRow);

		rows.forEach((row) => this.justifyRow(row));
	}

	/** return true if the passed in node was actually moved (checks for no-op and locked) */
	moveNode(node: GridStackNode, o: GridStackMoveOpts): boolean {
		if (this.rowResizingConfig) {
			const result = super.moveNode(node, o);
			if (result) {
				this.resizeNeighbours(node);
			}
			return result;
		} else if (this.draggingConfig) {
			this.batchUpdate(true, false);

			if (o.y === -1) {
				o.x = 0;
				o.w = this.column;
				this.justifyRows(this.draggingConfig.topNeighbours, node.h ?? 1, (node.y ?? 0) + (node.h ?? 0));
				super.moveNode(node, o);
			} else {
				const row = this.getNearestRow(o.y ?? node.y ?? 0);
				const sameRow = row.nodes.includes(node);
				const availableSpace = sameRow ? row.availableSpace + (node.w ?? 1) : row.availableSpace;
				const noSpaceInRow = !sameRow && row.availableSpace - (node.minW ?? 1) < 0;

				if (noSpaceInRow) {
					this.insertNodeBeforeRow(node, row);
				} else {
					o.y = row.y;
					o.w = Math.min(node.w ?? 1, availableSpace);

					const h = Math.max(row.height, node.minH ?? 1);
					if (h !== row.height) {
						this.nodes.forEach((gridNode) => {
							if (node !== gridNode && (gridNode.y ?? 0) === row.y) {
								super.moveNode(gridNode, { h });
							} else if (node !== gridNode && (gridNode.y ?? 0) >= row.y) {
								super.moveNode(gridNode, { y: (gridNode.y ?? 0) + h });
							}
						});
					}
					o.h = h;

					const lastX = row.nodes.at(-1)?.x ?? this.column;
					if (sameRow && o.x && (o.x < 0 || o.x > lastX)) {
						const nodes = row.nodes.filter((rowNode) => rowNode !== node);
						const startX = o.x < 0 ? o.w ?? 1 : 0;
						this.justifyNodes(nodes, startX, this.column - (o.w ?? 1), node.y ?? 0, node.h ?? 1);
					}
					super.moveNode(node, o);
				}
			}

			this.justify();

			this.batchUpdate(false, false);

			return true;
		} else {
			const result = super.moveNode(node, o);
			if (result) {
				this.justify();
			}
			return result;
		}
	}
}

//based on the original resizeToContent in GridStack. The only change is applying scaling to the height
GridStack.resizeToContentCB = (el: GridItemHTMLElement) => {
	if (!el) return;
	el.classList.remove('size-to-content-max');
	if (!el.clientHeight) return; // 0 when hidden, skip
	const n = el.gridstackNode;
	if (!n) return;
	const grid = n.grid;
	if (!grid || el.parentElement !== grid.el) return; // skip if we are not inside a grid
	let cell = grid.getCellHeight(true);
	if (!cell) return;

	const scale = el.getBoundingClientRect().width / el.offsetWidth;
	cell = cell * scale;

	let height = n.h ? n.h * cell : el.clientHeight; // getBoundingClientRect().height seem to flicker back and forth
	let item: Element | null | undefined;
	if (n.resizeToContentParent) {
		item = el.querySelector(n.resizeToContentParent);
	}
	if (!item) item = el.querySelector(GridStack.resizeToContentParent);
	if (!item) return;
	const padding = el.clientHeight - item.clientHeight; // full - available height to our child (minus border, padding...)

	const itemH = n.h ? n.h * cell - padding : item.clientHeight; // calculated to what cellHeight is or will become (rather than actual to prevent waiting for animation to finish)

	// NOTE: clientHeight & getBoundingClientRect() is undefined for text and other leaf nodes. use <div> container!
	const child = item.firstElementChild;
	if (!child) {
		console.error(
			`Error: GridStack.resizeToContent() widget id:${n.id} '${GridStack.resizeToContentParent}'.firstElementChild is null, make sure to have a div like container. Skipping sizing.`
		);
		return;
	}
	const wantedH = child.getBoundingClientRect().height || itemH;
	if (itemH === wantedH) return;
	height += wantedH - itemH;
	let h = Math.ceil(height / cell);
	// check for min/max and special sizing
	const softMax = Number.isInteger(n.sizeToContent) ? (n.sizeToContent as number) : 0;
	if (softMax && h > softMax) {
		h = softMax;
		el.classList.add('size-to-content-max'); // get v-scroll back
	}
	if (n.minH && h < n.minH) h = n.minH;
	else if (n.maxH && h > n.maxH) h = n.maxH;
	if (h !== n.h) {
		// @ts-expect-error gridstack types are wrong
		grid._ignoreLayoutsNodeChange = true;
		// @ts-expect-error gridstack types are wrong
		grid.moveNode(n, { h });
		// @ts-expect-error gridstack types are wrong
		delete grid._ignoreLayoutsNodeChange;
	}
};
